import { CBCT_SectionOrientation } from '@/shared/api/protocol-ts/model/dto_asset_common_pb';
import { AnalyticsEventName } from '@/shared/config';
import type {
  FullHeadMPRPaneLayout,
  FullHeadMPRProjection,
  ToothBasisSeriesName,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { IconNames } from '@/shared/ui';

import { MPRAnalyticsScreen, MPRAnnotationTool, MPRTool } from './types';

export const MPR_TOOL_ICON_SIZE = 25;
export { FULL_HEAD_MPR_PLANE_TOLERANCE } from '@/shared/graphics/medicalImageRender/MedicalImageRender';

export const DEFAULT_FULL_HEAD_MPR_PANE_LAYOUT: FullHeadMPRPaneLayout =
  'one_plus_two';

export const FULL_HEAD_MPR_PANE_LAYOUTS: FullHeadMPRPaneLayout[] = [
  'one_plus_two',
  'three_columns',
];

export const MPR_PANE_LAYOUT_ICONS: Record<FullHeadMPRPaneLayout, IconNames> = {
  one_plus_two: 'layoutOnePlusTwo',
  three_columns: 'layoutThreeColumns',
};

export const MPR_NEXT_PANE_LAYOUT: Record<
  FullHeadMPRPaneLayout,
  FullHeadMPRPaneLayout
> = {
  one_plus_two: 'three_columns',
  three_columns: 'one_plus_two',
};

export const FULL_HEAD_MPR_PROJECTION_TO_ORIENTATION: Record<
  FullHeadMPRProjection,
  CBCT_SectionOrientation
> = {
  axial: CBCT_SectionOrientation.SectionOrientationAxial,
  coronal: CBCT_SectionOrientation.SectionOrientationVestibuloOral,
  sagittal: CBCT_SectionOrientation.SectionOrientationMesioDistal,
};

export const FULL_HEAD_MPR_PROJECTION_TO_SERIES: Record<
  FullHeadMPRProjection,
  ToothBasisSeriesName
> = {
  axial: 'axial',
  coronal: 'frontal',
  sagittal: 'sagittal',
};

export const SERIES_TO_ORIENTATION: Record<
  ToothBasisSeriesName,
  CBCT_SectionOrientation
> = {
  axial: CBCT_SectionOrientation.SectionOrientationAxial,
  sagittal: CBCT_SectionOrientation.SectionOrientationVestibuloOral,
  frontal: CBCT_SectionOrientation.SectionOrientationMesioDistal,
};

export const ORIENTATION_TO_SERIES = Object.fromEntries(
  Object.entries(SERIES_TO_ORIENTATION).map(([seriesName, orientation]) => [
    orientation,
    seriesName,
  ]),
) as Partial<Record<CBCT_SectionOrientation, ToothBasisSeriesName>>;

export const MPR_TOOLS_BY_MODE_MAP: Record<MPRTool, IconNames> = {
  mainMode: 'move2',
  contrastBrightnessMode: 'brightness',
  panMode: 'movepawFilled',
  zoomMode: 'zoom',
  scrollThroughVolumeMode: 'split',
  rotateMode: 'turn',
  reset: 'rotate',
};

export const MPR_TOOLBAR_GROUPS = [
  ['mainMode', 'panMode', 'zoomMode', 'scrollThroughVolumeMode', 'rotateMode'],
  ['contrastBrightnessMode', 'sharpness'],
  ['ruler', 'angle', 'arrow'],
  ['paneLayout', 'download'],
  ['reset'],
] as const;

export const MPR_TOOL_SHORTCUTS: Partial<
  Record<MPRTool, { code: string; label: string }>
> = {
  contrastBrightnessMode: { code: 'KeyW', label: 'W' },
  panMode: { code: 'KeyM', label: 'M' },
  zoomMode: { code: 'KeyZ', label: 'Z' },
  scrollThroughVolumeMode: { code: 'KeyB', label: 'B' },
  rotateMode: { code: 'KeyR', label: 'R' },
  reset: { code: 'Digit0', label: '0' },
};

export const MPR_AXES_SHORTCUT = { code: 'KeyX', label: 'X' };
export const MPR_SHARPNESS_SHORTCUT = { code: 'KeyS', label: 'S' };

export const MPR_ARROW_CONTROL_SELECTOR =
  'input, [role="slider"], [role="spinbutton"], [role="radio"], [role="tab"], [role="menu"], [role="listbox"], [role="toolbar"]';

export const MPR_ANNOTATION_SHORTCUTS: Record<
  MPRAnnotationTool,
  { code: string; label: string }
> = {
  ruler: { code: 'KeyL', label: 'L' },
  angle: { code: 'KeyA', label: 'A' },
  arrow: { code: 'KeyY', label: 'Y' },
};

export const MPR_ANNOTATIONS_VISIBILITY_SHORTCUT = {
  code: 'KeyH',
  label: 'H',
};

export const MPR_TOOL_EVENTS: Record<
  MPRAnalyticsScreen,
  Record<MPRTool, AnalyticsEventName>
> = {
  toothPage: {
    mainMode: 'ToothPageMPRAxesButtonClicked',
    contrastBrightnessMode: 'ToothPageMPRBrightnessContrastButtonClicked',
    panMode: 'ToothPageMPRPanButtonClicked',
    zoomMode: 'ToothPageMPRZoomButtonClicked',
    scrollThroughVolumeMode: 'ToothPageMPRScrollButtonClicked',
    rotateMode: 'ToothPageMPRRotateButtonClicked',
    reset: 'ToothPageMPRResetButtonClicked',
  },
  reportPage: {
    mainMode: 'ReportPageMPRAxesButtonClicked',
    contrastBrightnessMode: 'ReportPageMPRBrightnessContrastButtonClicked',
    panMode: 'ReportPageMPRPanButtonClicked',
    zoomMode: 'ReportPageMPRZoomButtonClicked',
    scrollThroughVolumeMode: 'ReportPageMPRScrollButtonClicked',
    rotateMode: 'ReportPageMPRRotateButtonClicked',
    reset: 'ReportPageMPRResetButtonClicked',
  },
};

export const MPR_TOGGLE_EVENTS: Record<
  MPRAnalyticsScreen,
  {
    axes: AnalyticsEventName;
    zoomSync: AnalyticsEventName;
    paneLayout: AnalyticsEventName;
  }
> = {
  toothPage: {
    axes: 'ToothPageMPRAxesToggleClicked',
    zoomSync: 'ToothPageMPRZoomSyncToggleClicked',
    paneLayout: 'ToothPageMPRPaneLayoutSelected',
  },
  reportPage: {
    axes: 'ReportPageMPRAxesToggleClicked',
    zoomSync: 'ReportPageMPRZoomSyncToggleClicked',
    paneLayout: 'ReportPageMPRPaneLayoutSelected',
  },
};
